import { useQuery } from "@apollo/react-hooks";
import { Box, Grid, Hidden } from "@material-ui/core";
import {
  getOrgConfig,
  isFeatureEnabled
} from "graphql/featureFlags/featureFlags.gql";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { getUser } from "state";

import { StyledTab as Tab, StyledTabs as Tabs } from "./styled";
import {
  GetUserInExpiredOrgStatus,
  UserInfoQuery
} from "graphql/user/user.gql";
import { UserAssessmentCycleStatus } from "graphql/types/Cycle";
import ConfirmationModal from "../ConfirmationModal";
import { ReactComponent as LockIcon } from "assets/lock_icon.svg";

interface NavigationProps {
  setActive: React.Dispatch<React.SetStateAction<boolean>>;
  show: boolean;
}

const getTab = (
  location: string,
  isRefactorEnabled: boolean,
  sammonsUser: boolean,
  goldmanUser: boolean,
  isInExpiredOrg: boolean
): number | boolean => {
  // First check if refactor is enabled and user is not sammons user
  // If Index tab is hidden (If isRefactorEnabled is false OR the user is a Sammons/Goldman User), we need to adjust indices accordingly
  const indexTabHidden = !isRefactorEnabled || sammonsUser || goldmanUser;
  const trainingTabHidden = isInExpiredOrg;
  switch (location) {
    case "/dashboard":
      return 0;
    case "/":
      return 0;
    case "/index":
      return indexTabHidden ? false : 1;
    case "/training":
      // If Index tab is hidden, training should be index 1, otherwise 2
      //If training is hidden then it is false
      return trainingTabHidden ? false : indexTabHidden ? 1 : 2;
    case "/resources":
      // If Index tab is hidden, resources should be index 2, otherwise 3
      //If Training is hidden, then it is 2, or it is 3
      return trainingTabHidden ? 2 : indexTabHidden ? 2 : 3;
    default:
      return false;
  }
};

const Navigation: React.FC<NavigationProps> = ({
  setActive,
  show
}: NavigationProps) => {
  const history = useHistory();
  const location = history.location.pathname;

  const [isRefactorEnabled, setIsRefactorEnabled] = useState<boolean>(false);
  const [isAssessmentEnabled, setIsAssessmentEnabled] = useState<boolean>(
    false
  );
  const [isResourcesEnabled, setIsResourcesEnabled] = useState<boolean>(false);
  const [isTrainingsEnabled, setIsTrainingsEnabled] = useState<boolean>(false);
  const [
    isAssessmentNotStartedOrInProgress,
    setIsAssessmentNotStartedOrInProgress
  ] = useState<boolean>(false);
  const [
    showAssessmentConfirmationModal,
    setShowAssessmentConfirmationModal
  ] = useState(false);

  useQuery(UserInfoQuery, {
    fetchPolicy: "no-cache",
    onCompleted: data => {
      const assessmentStatus =
        data?.me?.currentCycle?.userAssessmentCycle?.status;
      const requiredTeaser = data?.me?.organization?.requiredTeaser;
      const completedTeaser = data?.me?.completedTeaser;
      if (
        (!requiredTeaser || (requiredTeaser && completedTeaser)) &&
        (assessmentStatus === UserAssessmentCycleStatus.IN_PROGRESS ||
          assessmentStatus === UserAssessmentCycleStatus.NOT_STARTED)
      ) {
        setIsAssessmentNotStartedOrInProgress(true);
      } else {
        setIsAssessmentNotStartedOrInProgress(false);
      }
    }
  });

  useQuery(isFeatureEnabled, {
    variables: { input: { featureName: "Refactor" } },
    onCompleted: data => setIsRefactorEnabled(data.isFeatureEnabled.enabled)
  });

  const { data } = useQuery(getOrgConfig, {
    onCompleted: d => {
      setIsResourcesEnabled(d.getOrgConfig.isResourceEnabled);
      setIsTrainingsEnabled(d.getOrgConfig.isTrainingEnabled);
      setIsAssessmentEnabled(d.getOrgConfig.isAssessmentEnabled);
    }
  });

  const user = useSelector(getUser);
  const sammonsUser = user?.organization?.name === "sammons";
  const goldmanUser = user?.organization?.name === "goldman-sachs";

  const [pathsToShow, setPathsToShow] = useState<String[]>([
    "/dashboard",
    "/profile",
    "/resources",
    "/training",
    "/faq",
    "/courses",
    "/myjourney"
  ]);

  const [value, setValue] = useState<number | boolean>(false);
  useEffect(() => {
    const tab = getTab(
      location,
      isRefactorEnabled,
      sammonsUser,
      goldmanUser,
      isInExpiredOrg
    );
    if (!tab) {
      setActive(false);
    }
    setValue(tab);
  }, [
    location,
    setValue,
    setActive,
    isRefactorEnabled,
    sammonsUser,
    goldmanUser
  ]);

  const indexTabHidden = !isRefactorEnabled || sammonsUser || goldmanUser;

  const handleChange = (
    event: React.ChangeEvent<{}>,
    newValue: number
  ): void => {
    const isRestrictedTab =
      (isAssessmentEnabled &&
        isAssessmentNotStartedOrInProgress &&
        (newValue === 2 || newValue === 3)) ||
      (indexTabHidden && (newValue === 1 || newValue === 2));

    if (!isRestrictedTab) {
      setActive(true);
      setValue(newValue);
    }
  };

  useEffect(() => {
    if (isRefactorEnabled) {
      setPathsToShow([
        ...pathsToShow,
        ...(!(sammonsUser || goldmanUser) ? ["/index"] : [])
      ]);
    }
  }, [isRefactorEnabled]);

  useEffect(() => {
    if (data && data.getOrgConfig) {
      setIsResourcesEnabled(data.getOrgConfig.isResourcesEnabled);
      setIsTrainingsEnabled(data.getOrgConfig.isTrainingEnabled);
    }
  }, [data]);

  const isLocked = useMemo(
    () => isAssessmentNotStartedOrInProgress && isAssessmentEnabled,
    [isAssessmentNotStartedOrInProgress, isAssessmentEnabled]
  );

  const [isInExpiredOrg, setIsInExpiredOrg] = useState<boolean>(false);

  useQuery(GetUserInExpiredOrgStatus, {
    onCompleted: data => {
      if (data?.me?.isInExpiredOrg) {
        setIsInExpiredOrg(data?.me?.isInExpiredOrg);
      }
    }
  });
  const LockedTab = ({ label }: { label: string }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gridGap: 5,
        color: "#7B7979"
      }}
    >
      <LockIcon />
      {label}
    </Box>
  );

  return (
    <>
      {(pathsToShow.some(
        path => path === location || location.includes(path + "/")
      ) ||
        show) && (
        <Grid item>
          <Hidden mdDown>
            <Tabs
              aria-label="tabs navigation"
              onChange={handleChange}
              value={value}
              variant="fullWidth"
            >
              <Tab component={Link} to="/dashboard" label="Dashboard" />
              {isRefactorEnabled && !(sammonsUser || goldmanUser) && (
                <Tab component={Link} to="/index" label="Index" />
              )}
              {isTrainingsEnabled && !isInExpiredOrg && (
                <Tab
                  {...(isLocked
                    ? {
                        onClick: () => setShowAssessmentConfirmationModal(true),
                        label: <LockedTab label="Training" />
                      }
                    : {
                        component: Link,
                        to: "/training",
                        label: "Training"
                      })}
                />
              )}

              {isResourcesEnabled && (
                <Tab
                  {...(isLocked
                    ? {
                        onClick: () => setShowAssessmentConfirmationModal(true),
                        label: <LockedTab label="Resources" />
                      }
                    : {
                        component: Link,
                        to: "/resources",
                        label: "Resources"
                      })}
                />
              )}
            </Tabs>
          </Hidden>
          {showAssessmentConfirmationModal && (
            <ConfirmationModal
              title="Complete Your Index"
              content="Index data drives research—finish to unlock  access this section "
              confirmText="Go to Assessment"
              cancelText="Return"
              onConfirm={async () => {
                setShowAssessmentConfirmationModal(false);
                history.push("/assessment-overview");
              }}
              onCancel={() => setShowAssessmentConfirmationModal(false)}
            />
          )}
        </Grid>
      )}
    </>
  );
};

export default Navigation;
